import React, { useState, useEffect } from 'react';
import { CodelcoLayout } from '../../components/codelco/CodelcoLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { RefreshCw, Download, CheckCircle2, XCircle, Eye, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CodelcoBitacoraPage() {
    const [envios, setEnvios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [estado, setEstado] = useState('');
    const [search, setSearch] = useState('');
    const [verPayload, setVerPayload] = useState(null);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [estado]);

    const load = async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams();
            if (estado) q.append('estado', estado);
            q.append('limit', '300');
            const r = await fetch(`${API_URL}/api/codelco/envios?${q}`, { headers });
            if (r.ok) setEnvios(await r.json());
        } catch { /* ignore */ }
        setLoading(false);
    };

    const exportCSV = () => {
        const rows = [['Fecha', 'Patente', 'Estado', 'Mensaje']];
        envios.filter(filterFn).forEach(e => {
            rows.push([
                new Date(e.created_at).toLocaleString(),
                e.patente || '',
                e.estado,
                (e.mensaje || '').replace(/"/g, '""').replace(/\n/g, ' '),
            ]);
        });
        const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bitacora_codelco_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const filterFn = e => !search || `${e.patente} ${e.mensaje}`.toLowerCase().includes(search.toLowerCase());
    const filtered = envios.filter(filterFn);

    return (
        <CodelcoLayout>
            <div className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-3xl font-black tracking-tight">Bitácora de envíos</h1>
                        <p className="text-muted-foreground">{filtered.length} registros mostrados</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={load} data-testid="btn-refresh">
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Refrescar
                        </Button>
                        <Button variant="outline" onClick={exportCSV} data-testid="btn-export-csv">
                            <Download className="h-4 w-4 mr-2" /> CSV
                        </Button>
                    </div>
                </div>

                <Card className="border-2">
                    <CardHeader className="pb-3">
                        <div className="flex gap-2 flex-wrap items-center">
                            <Filter className="h-4 w-4 text-muted-foreground" />
                            {[
                                { v: '', l: 'Todos' },
                                { v: 'ok', l: 'OK' },
                                { v: 'error', l: 'Error' },
                            ].map(f => (
                                <Button key={f.v} variant={estado === f.v ? 'default' : 'outline'} size="sm" onClick={() => setEstado(f.v)} data-testid={`filter-${f.v || 'all'}`}>
                                    {f.l}
                                </Button>
                            ))}
                            <div className="flex-1 min-w-[200px]">
                                <Input placeholder="Buscar patente o mensaje..." value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="p-8 text-center text-muted-foreground">Cargando...</div>
                        ) : filtered.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">Sin envíos registrados</div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Fecha/Hora</TableHead>
                                        <TableHead>Patente</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead>Respuesta</TableHead>
                                        <TableHead className="text-right">Payload</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(e => (
                                        <TableRow key={e.id}>
                                            <TableCell className="text-xs whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</TableCell>
                                            <TableCell className="font-mono font-bold">{e.patente || '-'}</TableCell>
                                            <TableCell>
                                                {e.estado === 'ok' ? (
                                                    <Badge className="bg-emerald-500 text-white"><CheckCircle2 className="h-3 w-3 mr-1" /> OK</Badge>
                                                ) : (
                                                    <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Error</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs max-w-md truncate" title={e.mensaje}>
                                                {(e.mensaje || '').substring(0, 200)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="icon" onClick={() => setVerPayload(e)}>
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Dialog open={!!verPayload} onOpenChange={(v) => !v && setVerPayload(null)}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Detalle envío — {verPayload?.patente}</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto space-y-3 text-xs flex-1">
                        <div>
                            <div className="font-bold mb-1">Fecha</div>
                            <div className="text-muted-foreground">{verPayload && new Date(verPayload.created_at).toLocaleString()}</div>
                        </div>
                        <div>
                            <div className="font-bold mb-1">Estado</div>
                            <div>{verPayload?.estado}</div>
                        </div>
                        <div>
                            <div className="font-bold mb-1">Respuesta WS</div>
                            <pre className="bg-muted p-3 rounded whitespace-pre-wrap break-all">{verPayload?.mensaje}</pre>
                        </div>
                        <div>
                            <div className="font-bold mb-1">Payload XML enviado</div>
                            <pre className="bg-muted p-3 rounded whitespace-pre-wrap break-all text-[10px] max-h-80 overflow-y-auto">{verPayload?.payload || '(no guardado)'}</pre>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </CodelcoLayout>
    );
}
